import { ModuleWrapper } from '@/components'
import { FullPageLoader } from '@/components_deprecated'
import { Module } from '@/modules/companies'
import { asNullableString, asString, asStringArray } from '@/utils'
import {
  useLeaderGroupsSuspense,
  useOnErrorAuto,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Suspense, useMemo } from 'react'
import { ALL_DRIVER_FORECASTS_QUERY } from '../queries'
import { filterForecasts } from '../remaining-driving-time/remaining-driving-time'
import type { RemainingDrivingTimeFilterForm } from '../remaining-driving-time/remaining-driving-time-filters'
import { RemainingDrivingTimeFilters } from '../remaining-driving-time/remaining-driving-time-filters'
import type { ForecastRow } from '../remaining-driving-time/remaining-driving-time-table'
import { RemainingDrivingTimeTable } from '../remaining-driving-time/remaining-driving-time-table'

const RemainingDrivingTimeInner: React.FC = () => {
  const translations = useTranslate({
    remainingDriving: 'common.remaining-driving-time',
  })

  const me = useUser()
  const { hasPermissions } = usePermissions()
  const canSeeOthers = hasPermissions(
    PERMISSIONS.activities.change.driveractivity,
    true
  )

  const leaderGroups = useLeaderGroupsSuspense(Module.ACTIVITIES)

  const { formValues: filter, updateForm } =
    useForm<RemainingDrivingTimeFilterForm>({
      values: {
        users: [],
        leaderGroup: null,
        orderBy: '',
      },
      config: {
        storage: {
          storeFormState: true,
          storeFormStateName: 'remaining-driving-time-filters',
          sessionStorage: true,
          retrieveFormStateOnMount: true,
          transformValues: {
            users: asStringArray,
            leaderGroup: asNullableString,
            orderBy: asString,
          },
        },
      },
    })

  const onErrorAuto = useOnErrorAuto()
  const { data, loading } = useQuery(ALL_DRIVER_FORECASTS_QUERY, {
    onError: onErrorAuto(),
  })

  const forecasts = useMemo<ForecastRow[]>(() => {
    const leaderGroupUserIds = filter.leaderGroup
      ? leaderGroups.myLeaderGroups
          .find(group => group.id === filter.leaderGroup)
          ?.users.map(user => user.id) ?? []
      : null

    return filterForecasts(data?.allForecasts ?? [], {
      users: canSeeOthers ? filter.users : [me.id],
      leaderGroupUserIds: canSeeOthers ? leaderGroupUserIds : null,
    }).map(forecast => ({ ...forecast, id: forecast.user.id }))
  }, [
    canSeeOthers,
    data?.allForecasts,
    filter.leaderGroup,
    filter.users,
    leaderGroups.myLeaderGroups,
    me.id,
  ])

  return (
    <ModuleWrapper title={translations.remainingDriving}>
      {canSeeOthers && (
        <RemainingDrivingTimeFilters
          filter={filter}
          updateForm={updateForm}
          leaderGroups={leaderGroups}
        />
      )}

      <RemainingDrivingTimeTable
        forecasts={forecasts}
        loading={loading && !data}
        orderBy={filter.orderBy}
        onOrderByChange={orderBy => updateForm({ orderBy })}
      />
    </ModuleWrapper>
  )
}

export const RemainingDrivingTime: React.FC = () => (
  <Suspense fallback={<FullPageLoader />}>
    <RemainingDrivingTimeInner />
  </Suspense>
)
